/**
 * App shell — JuiceProvider + IdleAttractorScope("app") + AtmosphereShell +
 * AppChrome, with the wouter Switch inside. The shell (chrome + stadium
 * atmosphere CSS) paints instantly from the service worker; routes arrive as
 * choreography (KICKOFF §3.3).
 *
 * AppChrome (24 Jul, real-device review) replaces the old always-on
 * BroadcastChrome prize banner — that component was Landing's own "GAMES
 * NIGHT · LIVE" treatment mounted globally by mistake (Landing has always had
 * its own inline eyebrow; the LIVE-bug/prize duo never belonged in the
 * shell). AppChrome is the actual persistent chrome the comp specifies:
 * hamburger → drawer (Locker Room, Trophy Room, Match Centre, Night Room,
 * Help, VAR, Terms) + a bell wired to the real inbox count. It self-gates on
 * session + route (renders nothing pre-auth or on full-bleed screens).
 *
 * screen_viewed fires on every route change; session_started once per boot
 * (lib/platform). OfflineBanner rides navigator.onLine.
 *
 * Route transitions (24 Jul, "the site doesn't feel seamless" finding):
 * navigation used to cut instantly — Switch just unmounts the old route and
 * mounts the new one, no animation either side. RouteRise wraps it in the
 * SAME rise entrance packages/ui already built for SkeletonToContent (see
 * that component's own header for why a plain key+className would silently
 * break every fixed-position overlay in the app instead).
 */
import { Suspense, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { AtmosphereShell, OfflineBanner, Skeleton } from "@matchday/ui";
import { IdleAttractorScope, JuiceProvider, juice } from "@matchday/juice";
import Landing from "./routes/Landing";
import { LAZY_ROUTES } from "./routes";
import { AppChrome, navSuppressedFor } from "./components/chrome/AppChrome";
import { BottomNav } from "./components/chrome/BottomNav";
import { ResultLockSheet } from "./components/chrome/ResultLockSheet";
import { useResultLock } from "./lib/useResultLock";
import { bounceTarget, matchPath, navBlocked } from "./lib/resultLock";
import { LiveTicker } from "./components/chrome/LiveTicker";
import { RouteRise } from "./components/chrome/RouteRise";
import { emitSessionStarted, trackScreen, tracker } from "./lib/platform";

function useOnline(): { online: boolean; queued: number } {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [queued, setQueued] = useState(() => tracker.pending());

  useEffect(() => {
    const sync = () => {
      setOnline(navigator.onLine);
      setQueued(tracker.pending());
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  return { online, queued };
}

export default function App() {
  const [location, navigate] = useLocation();
  const { online, queued } = useOnline();

  // Once per boot, with source derived from the ENTRY url (deeplink/share/organic).
  useEffect(() => {
    void emitSessionStarted(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every route change is a screen view.
  useEffect(() => {
    trackScreen(location);
  }, [location]);

  /**
   * Land every screen at the top (BK device test, p1: "check the alignment on
   * the page to fit to screen when re-load/action absorbed").
   *
   * The app had NO scroll handling of any kind, which shows up two ways:
   *
   *  - NAVIGATION. Scroll down the feed, open the inbox, and the inbox opens at
   *    the old scroll offset — mid-page, header off-screen. Nothing was ever
   *    resetting it, so this happened on every route change in the app.
   *
   *  - RELOAD. Browsers restore the previous scroll offset automatically, but
   *    this is an SPA whose content arrives after the first paint. The browser
   *    restores against a nearly-empty document, the real content lands
   *    underneath it, and the player is left parked somewhere arbitrary.
   *    `scrollRestoration = "manual"` hands that decision to us.
   *
   * Instant, not smooth: a new screen should already BE at the top when it
   * appears, not visibly travel there while RouteRise is playing its entrance.
   */
  /**
   * THE RESULT LOCK (BK, 28 Jul). A player holding an unreported match is kept
   * on it: nav taps raise the prompt, and any other screen sends them back.
   *
   * Disabled on the pre-auth and ceremony routes for the same reason the nav is
   * — someone mid-signup cannot owe a result, and a modal over an onboarding
   * ceremony would be a lock on a door they have not reached yet.
   */
  const lockEnabled = !navSuppressedFor(location);
  const lock = useResultLock(lockEnabled);
  const [lockSheet, setLockSheet] = useState(false);
  const lockInput = { owedMatchId: lock.owedMatchId, loaded: lock.loaded, path: location };

  // "Always brought back to the same match." Anywhere but the owed match, they
  // are returned to it. bounceTarget returns null when they are already there,
  // which is what keeps this from looping against itself.
  useEffect(() => {
    if (!lockEnabled) return;
    const target = bounceTarget(lockInput);
    if (target !== null) navigate(target, { replace: true });
  }, [lockEnabled, lock.owedMatchId, lock.loaded, location, navigate]);

  // The browser back button is a nav attempt like any other. Without this, back
  // is the one exit the lock does not cover — and it is the exit people use.
  useEffect(() => {
    if (!navBlocked(lockInput)) return;
    // A pushed entry to swallow the first Back, re-armed each time it is spent.
    history.pushState({ resultLock: true }, "");
    const onPop = () => {
      setLockSheet(true);
      history.pushState({ resultLock: true }, "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [lock.owedMatchId, lock.loaded, location]);

  useEffect(() => {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [location]);

  return (
    <JuiceProvider service={juice}>
      <IdleAttractorScope name="app">
        <AtmosphereShell>
          <OfflineBanner visible={!online} queuedCount={queued} />
          <AppChrome />
          <Suspense
            fallback={
              <div className="pl-route-fallback">
                <Skeleton variant="panel" clip="bevel" height={220} />
              </div>
            }
          >
            <RouteRise routeKey={location}>
              <Switch>
                <Route path="/" component={Landing} />
                {LAZY_ROUTES.map(({ path, component }) => (
                  <Route key={path} path={path} component={component} />
                ))}
                <Route>
                  {/* Unknown routes fall back to the landing surface — a magic
                      link with a mangled path must never dead-end (MATCH POINT). */}
                  <Landing />
                </Route>
              </Switch>
            </RouteRise>
          </Suspense>
          {/* Global chrome, same gate as AppChrome: a full-bleed ceremony or the
              match centre must not get a nav bar bolted to its bottom edge. */}
          {navSuppressedFor(location) ? null : (
            <>
              <LiveTicker />
              <BottomNav
                onIntercept={() => {
                  if (!navBlocked(lockInput)) return false;
                  setLockSheet(true);
                  return true;
                }}
              />
            </>
          )}
          <ResultLockSheet
            open={lockSheet && navBlocked(lockInput)}
            kind={lock.kind}
            onDismiss={() => setLockSheet(false)}
            onSubmit={() => {
              setLockSheet(false);
              if (lock.owedMatchId !== null) navigate(matchPath(lock.owedMatchId));
            }}
          />
        </AtmosphereShell>
      </IdleAttractorScope>
    </JuiceProvider>
  );
}
